export const MAX_PROPOSAL_SERVICE_AREA_ITEMS = 100;
export const MAX_PROPOSAL_SERVICE_AREA_ITEM_LENGTH = 500;

export const DEFAULT_PROPOSAL_SERVICE_AREA_ITEMS = [
	'Carpeted communal hallways (15 floors), including internal and fire doors',
	'Two lifts',
	'Fire escape staircase',
	'Main hallway entrance',
	'Full-height glass wall entrance doors',
	'Post point',
	'Entrance porch area',
];

export const DEFAULT_PROPOSAL_WEEKLY_TASKS = [
	'Vacuuming all carpeted areas',
	'Sweeping, vacuuming and mopping hard floor areas',
	'Dusting and wiping surfaces, fixtures, skirting boards and windowsills',
	'Wiping internal communal doors',
	'Disinfecting high-touch surfaces (door handles, lift buttons, handrails)',
	'Lift cleaning (doors, walls, floors, mirrors, control panels)',
	'Cleaning of all staircases, landings, and handrails',
	'Removing marks and fingerprints from glass elements, including the main entrance door, internal windows and doors, and rear doors',
	'Wiping post boxes',
	'Vacuuming soft furniture in the main entrance',
	'Replacing bin liners in all accessible bins',
	'Sweeping and tidying entrance porch areas',
];

export const DEFAULT_PROPOSAL_MONTHLY_TASKS = [
	'Cobweb removal in communal areas*',
	'Dusting light fixtures*',
	'Cleaning internal and external glass entrance doors in the main entrance hallway*',
];
export const DEFAULT_PROPOSAL_MONTHLY_TASKS_FOOTNOTE =
	'*Safe reachable height only, without specialist equipment.';

export const DEFAULT_PROPOSAL_PRICING_NOTES = [
	'Services are delivered within the agreed weekly hours. Any additional works or hours will be agreed in writing in advance.',
	'Fees are subject to periodic review.',
];

const itemKey = (value) => String(value ?? '').trim().toLocaleLowerCase();

export const normalizeProposalServiceAreaItemsTemplate = (values) => {
	if (!Array.isArray(values)) return [];

	const normalized = [];
	const seen = new Set();
	for (const value of values) {
		if (normalized.length >= MAX_PROPOSAL_SERVICE_AREA_ITEMS) break;
		const item = String(value ?? '')
			.trim()
			.slice(0, MAX_PROPOSAL_SERVICE_AREA_ITEM_LENGTH);
		const key = itemKey(item);
		if (!item || seen.has(key)) continue;
		seen.add(key);
		normalized.push(item);
	}
	return normalized;
};

export const getProposalServiceAreaItemsTemplate = (documentPresets) => {
	const proposalSettings = documentPresets?.proposal;
	if (Array.isArray(proposalSettings?.serviceAreaItemsTemplate)) {
		return normalizeProposalServiceAreaItemsTemplate(
			proposalSettings.serviceAreaItemsTemplate,
		);
	}
	return [...DEFAULT_PROPOSAL_SERVICE_AREA_ITEMS];
};

const getProposalTaskItemsTemplate = (documentPresets, field, fallback) => {
	const proposalSettings = documentPresets?.proposal;
	if (Array.isArray(proposalSettings?.[field])) {
		return normalizeProposalServiceAreaItemsTemplate(proposalSettings[field]);
	}
	return [...fallback];
};

export const getProposalWeeklyTasksTemplate = (documentPresets) =>
	getProposalTaskItemsTemplate(
		documentPresets,
		'weeklyTasksTemplate',
		DEFAULT_PROPOSAL_WEEKLY_TASKS,
	);

export const getProposalMonthlyTasksTemplate = (documentPresets) =>
	getProposalTaskItemsTemplate(
		documentPresets,
		'monthlyTasksTemplate',
		DEFAULT_PROPOSAL_MONTHLY_TASKS,
	);

export const getProposalPricingNotesTemplate = (documentPresets) =>
	getProposalTaskItemsTemplate(
		documentPresets,
		'pricingNotesTemplate',
		DEFAULT_PROPOSAL_PRICING_NOTES,
	);

export const mergeProposalServiceAreaItemsTemplate = (documentPresets, values) => {
	const rootSettings =
		documentPresets && typeof documentPresets === 'object' && !Array.isArray(documentPresets)
			? documentPresets
			: {};
	const proposalSettings =
		rootSettings.proposal &&
		typeof rootSettings.proposal === 'object' &&
		!Array.isArray(rootSettings.proposal)
			? rootSettings.proposal
			: {};

	return {
		...rootSettings,
		version: 1,
		proposal: {
			...proposalSettings,
			serviceAreaItemsTemplate: normalizeProposalServiceAreaItemsTemplate(values),
		},
	};
};

const mergeProposalTaskItemsTemplate = (documentPresets, field, values) => {
	const rootSettings =
		documentPresets && typeof documentPresets === 'object' && !Array.isArray(documentPresets)
			? documentPresets
			: {};
	const proposalSettings =
		rootSettings.proposal &&
		typeof rootSettings.proposal === 'object' &&
		!Array.isArray(rootSettings.proposal)
			? rootSettings.proposal
			: {};

	return {
		...rootSettings,
		version: 1,
		proposal: {
			...proposalSettings,
			[field]: normalizeProposalServiceAreaItemsTemplate(values),
		},
	};
};

export const mergeProposalWeeklyTasksTemplate = (documentPresets, values) =>
	mergeProposalTaskItemsTemplate(documentPresets, 'weeklyTasksTemplate', values);

export const mergeProposalMonthlyTasksTemplate = (documentPresets, values) =>
	mergeProposalTaskItemsTemplate(documentPresets, 'monthlyTasksTemplate', values);

export const mergeProposalPricingNotesTemplate = (documentPresets, values) =>
	mergeProposalTaskItemsTemplate(documentPresets, 'pricingNotesTemplate', values);

export const appendNewProposalServiceAreaItemsFromCatalog = (
	previousCatalog,
	savedCatalog,
	currentItems,
) => {
	const previousKeys = new Set(
		normalizeProposalServiceAreaItemsTemplate(previousCatalog).map(itemKey),
	);
	const nextItems = Array.isArray(currentItems) ? [...currentItems] : [];
	const currentKeys = new Set(nextItems.map(itemKey).filter(Boolean));

	for (const item of normalizeProposalServiceAreaItemsTemplate(savedCatalog)) {
		const key = itemKey(item);
		if (previousKeys.has(key) || currentKeys.has(key)) continue;
		currentKeys.add(key);
		nextItems.push(item);
	}

	return nextItems;
};

export const appendNewProposalTaskItemsFromCatalog = (
	previousCatalog,
	savedCatalog,
	currentItems,
) => appendNewProposalServiceAreaItemsFromCatalog(
	previousCatalog,
	savedCatalog,
	currentItems,
);

export const applyProposalTaskTemplatesToNewContent = (content, documentPresets) => {
	const source = content && typeof content === 'object' && !Array.isArray(content) ? content : {};
	const weeklyTasks = getProposalWeeklyTasksTemplate(documentPresets);
	const monthlyTasks = getProposalMonthlyTasksTemplate(documentPresets);
	const pricingNotes = getProposalPricingNotesTemplate(documentPresets);
	const pricing =
		source.pricing && typeof source.pricing === 'object' && !Array.isArray(source.pricing)
			? source.pricing
			: {};
	const taskSections = Array.isArray(source.taskSections)
		? source.taskSections.map((section) => {
			const title = itemKey(section?.title);
			if (title === 'weekly tasks') {
				return { ...section, items: [...weeklyTasks] };
			}
			if (title === 'monthly tasks') {
				return { ...section, items: [...monthlyTasks] };
			}
			return section;
		})
		: source.taskSections;

	return {
		...source,
		taskSections,
		pricing: {
			...pricing,
			notes: [...pricingNotes],
		},
	};
};
